import { useState } from 'react'
import { Crown, CheckCircle2, Clock, XCircle, RefreshCw, Eye } from 'lucide-react'
import { Badge, Button, Card, CardContent } from '@/components/ui'
import { SectionHeading } from '@/components/common'
import { useAdminSubscriptions } from '../../hooks/use-admin'
import { EnterpriseTable, type Column } from '../../components/tables/enterprise-table'
import { UserDetailDrawer } from '../../components/dialogs/user-detail-drawer'
import type { AdminSubscription, AdminUser } from '../../types/admin.types'

export function SubscriptionsPage() {
  const { data: subOverview, isLoading } = useAdminSubscriptions()

  const [inspectUser, setInspectUser] = useState<AdminUser | null>(null)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  const handleInspectUser = (user: AdminUser) => {
    setInspectUser(user)
    setIsDrawerOpen(true)
  }

  const columns: Column<AdminSubscription>[] = [
    {
      key: 'user',
      header: 'Subscriber',
      sortable: true,
      sortValue: (s) => `${s.user?.firstName ?? ''} ${s.user?.lastName ?? ''}`,
      accessor: (s) => (
        <div className="flex items-center gap-3">
          <div className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-amber-500/10 text-amber-500 font-display text-xs font-bold">
            <Crown className="size-4" />
          </div>
          <div className="min-w-0">
            <p className="truncate font-semibold text-foreground">
              {s.user?.firstName} {s.user?.lastName}
            </p>
            <p className="truncate text-xs text-muted-foreground">{s.user?.email}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'plan',
      header: 'Subscription Plan',
      sortable: true,
      sortValue: (s) => s.plan?.name ?? '',
      accessor: (s) => (
        <Badge variant="default" className="font-bold">
          {s.plan?.name ?? 'Standard Plan'}
        </Badge>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      accessor: (s) => (
        <Badge
          variant={
            s.status === 'ACTIVE'
              ? 'success'
              : s.status === 'PENDING'
              ? 'warning'
              : 'destructive'
          }
        >
          {s.status}
        </Badge>
      ),
    },
    {
      key: 'autoRenew',
      header: 'Auto Renew',
      accessor: (s) => (
        <div className="flex items-center gap-1 text-xs">
          <RefreshCw className={`size-3 ${s.autoRenew ? 'text-emerald-500' : 'text-muted-foreground'}`} />
          <span className={s.autoRenew ? 'font-semibold text-emerald-500' : 'text-muted-foreground'}>
            {s.autoRenew ? 'Enabled' : 'Disabled'}
          </span>
        </div>
      ),
    },
    {
      key: 'expiresAt',
      header: 'Expires On',
      sortable: true,
      sortValue: (s) => new Date(s.expiresAt).getTime(),
      accessor: (s) => (
        <span className="text-xs text-muted-foreground">
          {new Date(s.expiresAt).toLocaleDateString()}
        </span>
      ),
    },
  ]

  const filterOptions = [
    {
      key: 'status',
      label: 'Status',
      options: [
        { label: 'Active', value: 'ACTIVE' },
        { label: 'Pending', value: 'PENDING' },
        { label: 'Expired', value: 'EXPIRED' },
        { label: 'Cancelled', value: 'CANCELLED' },
      ],
      getValue: (s: AdminSubscription) => s.status,
    },
  ]

  return (
    <div className="space-y-8">
      {/* Header */}
      <SectionHeading
        eyebrow="Revenue & Tiers"
        title="Subscription Management"
        description="Monitor active plan memberships, billing cycles, auto-renewals, and expired subscriptions."
        align="left"
      />

      {/* Summary Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card className="border-border/80 bg-card/80 backdrop-blur-xl">
          <CardContent className="p-4 flex items-center gap-3">
            <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-amber-500/10 text-amber-500">
              <Crown className="size-5" />
            </span>
            <div>
              <p className="text-xs text-muted-foreground font-medium">Total Subscriptions</p>
              <p className="font-display text-xl font-bold text-foreground">
                {subOverview?.counts.total ?? 0}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/80 bg-card/80 backdrop-blur-xl">
          <CardContent className="p-4 flex items-center gap-3">
            <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-500">
              <CheckCircle2 className="size-5" />
            </span>
            <div>
              <p className="text-xs text-muted-foreground font-medium">Active Plans</p>
              <p className="font-display text-xl font-bold text-emerald-500">
                {subOverview?.counts.active ?? 0}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/80 bg-card/80 backdrop-blur-xl">
          <CardContent className="p-4 flex items-center gap-3">
            <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-yellow-500/10 text-yellow-500">
              <Clock className="size-5" />
            </span>
            <div>
              <p className="text-xs text-muted-foreground font-medium">Pending Upgrades</p>
              <p className="font-display text-xl font-bold text-foreground">
                {subOverview?.counts.pending ?? 0}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/80 bg-card/80 backdrop-blur-xl">
          <CardContent className="p-4 flex items-center gap-3">
            <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-red-500/10 text-red-500">
              <XCircle className="size-5" />
            </span>
            <div>
              <p className="text-xs text-muted-foreground font-medium">Expired / Cancelled</p>
              <p className="font-display text-xl font-bold text-foreground">
                {(subOverview?.counts.expired ?? 0) + (subOverview?.counts.cancelled ?? 0)}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Subscriptions Table */}
      <EnterpriseTable
        data={subOverview?.recentSubscriptions ?? []}
        columns={columns}
        isLoading={isLoading}
        searchPlaceholder="Search subscriber by name or email..."
        searchableKeys={[
          (s) => `${s.user?.firstName ?? ''} ${s.user?.lastName ?? ''} ${s.user?.email ?? ''} ${s.plan?.name ?? ''}`,
        ]}
        filterOptions={filterOptions}
        actions={(s) => (
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleInspectUser(s.user)}
            className="h-8 gap-1.5 text-xs font-semibold"
          >
            <Eye className="size-3.5" /> View Subscriber
          </Button>
        )}
        emptyTitle="No subscriptions active"
        emptyDescription="No subscriber matches the selected filter or search criteria."
      />

      {/* Subscriber Detail Drawer */}
      <UserDetailDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        user={inspectUser}
      />
    </div>
  )
}
